import { channelCustomTypes } from '@inbox-demo-nx/constants';
import useInboxChannelList, {
  Message,
} from '@inbox-demo-nx/hooks-use-inbox-channel-list';
import useInboxNavigationHeader from '@inbox-demo-nx/hooks-use-inbox-navigation-header';
import usePullToRefresh from '@inbox-demo-nx/hooks-use-pull-to-refresh';
import ChannelListItem from '@inbox-demo-nx/ui-channel-list-item';
import CircleProgress from '@inbox-demo-nx/ui-circle-progress';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Text, useSx, View } from 'dripsy';
import { StatusBar } from 'expo-status-bar';
import { useCallback, useEffect, useMemo } from 'react';
import { FlatList } from 'react-native';
import SendBird from 'sendbird';

const isBannerChannel = (channel: SendBird.GroupChannel) =>
  channel.customType === channelCustomTypes.banner;

export default function InboxScreen() {
  const { data, isLoading, refetch, error, dataUpdatedAt } =
    useInboxChannelList({
      shouldIncludeRecentMessages: isBannerChannel,
    });
  const pullToRefreshProps = usePullToRefresh({ isLoading, refetch });
  const navigation = useNavigation<NativeStackNavigationProp<any>>();

  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [refetch])
  );

  useEffect(() => {
    error && console.error(error);
  }, [error]);

  const sx = useSx();
  useInboxNavigationHeader();

  const { channels } = useMemo(() => {
    if (isLoading && data.length === 0) {
      return { channels: [], banners: [] };
    }

    return {
      channels: data
        .filter((item) => !isBannerChannel(item.channel))
        .map((item) => item.channel),
      banners: data
        .filter((item) => isBannerChannel(item.channel))
        .flatMap((item) => item.recentMessages)
        .filter((v): v is Message => !!v),
    };
  }, [data, isLoading]);

  const ListEmptyComponent = (
    <View
      style={{
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {isLoading ? (
        <CircleProgress size={32} />
      ) : (
        <Text sx={{ fontWeight: '600', fontSize: 'medium' }}>No channels</Text>
      )}
    </View>
  );

  return (
    <View sx={{ backgroundColor: 'background', flex: 1 }}>
      <StatusBar style="dark" />
      <FlatList<SendBird.GroupChannel>
        style={{ flex: 1 }}
        data={channels}
        extraData={dataUpdatedAt}
        keyExtractor={(item) => item.url}
        renderItem={({ item: channel }) => (
          <ChannelListItem
            channel={channel}
            onPress={() => {
              navigation.push('Chat', { channelUrl: channel.url });
            }}
          />
        )}
        ListEmptyComponent={ListEmptyComponent}
        contentContainerStyle={sx({ flexGrow: 1, pt: 8, pb: 48 })}
        {...pullToRefreshProps}
      />
    </View>
  );
}
